import { useMemo } from 'react'
import { Money } from 'lib/money'
import {
    certificatesToDiscounts,
    Discount,
} from 'containers/calculator/entities/discounts'
import { useCalculatorInput } from 'containers/calculator/hooks/useCalculatorInput'
import data from 'public/calculator-data.json'

const calculateDiscount = ({
    fixedFeeAmount,
    discounts,
}: {
    discounts?: Discount[]
    fixedFeeAmount: number
}) => {
    const discountPercentage =
        discounts?.reduce(
            (acc, current) => acc + certificatesToDiscounts[current.name],
            0,
        ) || 0

    return {
        discountPercentage,
        amount: Money.multiply(fixedFeeAmount, discountPercentage),
    }
}

const applyDiscounts = ({
    fixedFeeAmount,
    totalAmount,
    discounts,
}: {
    totalAmount: number
    discounts?: Discount[]
    fixedFeeAmount: number
}) => {
    const calculatedDiscount = calculateDiscount({ discounts, fixedFeeAmount })

    if (calculatedDiscount.discountPercentage === 0) {
        return { totalAmount, discountPercentage: 0, fixedFeeAmount }
    }

    return {
        totalAmount: Money.subtract(totalAmount, calculatedDiscount.amount),
        discountPercentage: calculatedDiscount.discountPercentage,
        fixedFeeAmount,
    }
}

export const useCalculatorResult = () => {
    const { values } = useCalculatorInput()

    const rate = useMemo(
        () =>
            data.find(
                item => item.term === values.term && item.bondRating === values.rating,
            )?.rate || 0,
        [values.term, values.rating],
    )

    const normalizedAmount = Money.normalize(values.amount)
    const fixedFeePercentage = rate

    const fixedFeeAmount = normalizedAmount * fixedFeePercentage
    const totalAmount = Money.add(normalizedAmount, fixedFeeAmount)

    const result = {
        loanAmount: values.amount,
        term: values.term,
        fixedFeePercentage,
        fixedFeeAmount,
        totalAmount,
        discountPercentage: 0,
    }

    return {
        standard: result,
        discounted: {
            ...result,
            ...applyDiscounts({
                fixedFeeAmount,
                discounts: values.discounts,
                totalAmount,
            }),
        },
    }
}
